/**
 * Detail pages need a "Back" button that points at the page the user
 * actually came from (People, Accounts, a traffic source, …) rather than a
 * single hardcoded fallback. Callers pass `state: { from: pathname }`
 * when they navigate to the detail page; this helper reads that hint and
 * returns a target + a friendly label for the button.
 *
 * Falls back to the section's index page (`/contacts`) when no hint is
 * provided so the back button never breaks.
 */
export interface BackTarget {
  to: string;
  label: string;
}

const KNOWN: { match: (p: string) => boolean; to: string; label: string }[] = [
  { match: (p) => p === "/" || p.startsWith("/?"), to: "/", label: "Home" },
  { match: (p) => p === "/inbox" || p.startsWith("/inbox/"), to: "/inbox", label: "Inbox" },
  { match: (p) => p === "/mail" || p.startsWith("/mail/"), to: "/mail", label: "Mail" },
  { match: (p) => p === "/campaigns" || p.startsWith("/campaigns/"), to: "/campaigns", label: "Campaigns" },
  { match: (p) => p === "/funnel" || p.startsWith("/funnel?"), to: "/funnel", label: "Funnel" },
  { match: (p) => p === "/work" || p.startsWith("/work/"), to: "/work", label: "Work" },
  { match: (p) => p === "/team" || p.startsWith("/team/"), to: "/team", label: "Team" },
  { match: (p) => p === "/affiliates" || p.startsWith("/affiliates/"), to: "/affiliates", label: "Affiliates" },
  { match: (p) => p === "/ads" || p.startsWith("/ads?"), to: "/ads", label: "Ads" },
  { match: (p) => p === "/outreach" || p.startsWith("/outreach?"), to: "/outreach", label: "Outreach" },
  { match: (p) => p === "/partners" || p.startsWith("/partners/"), to: "/partners", label: "Partners" },
  { match: (p) => p === "/creators" || p.startsWith("/creators/"), to: "/creators", label: "Creators" },
  { match: (p) => p === "/content" || p.startsWith("/content/"), to: "/content", label: "Social" },
  { match: (p) => p === "/lists" || p.startsWith("/lists/"), to: "/lists", label: "Smart lists" },
  { match: (p) => p === "/automations" || p.startsWith("/automations/"), to: "/automations", label: "Automations" },
  { match: (p) => p.startsWith("/settings"), to: "/settings", label: "Settings" },
  { match: (p) => p === "/traffic" || p.startsWith("/traffic/") || p.startsWith("/traffic?"), to: "/traffic", label: "Traffic" },
  { match: (p) => p === "/website" || p.startsWith("/website?"), to: "/traffic?tab=website", label: "Website" },
  { match: (p) => p === "/contacts" || p.startsWith("/contacts?"), to: "/contacts", label: "People" },
  // Platform accounts (the SyncSuite customer directory). Detail pages count
  // too: contact ⇄ account round-trips must land back on the exact record.
  { match: (p) => p === "/accounts" || p.startsWith("/accounts/") || p.startsWith("/accounts?"), to: "/accounts", label: "Accounts" },
  // Contact detail — so the ACCOUNT page's back button can return to the
  // exact contact the user came from.
  { match: (p) => p.startsWith("/contacts/"), to: "/contacts", label: "Contact" },
];

export function backFromState(state: unknown, fallback: BackTarget = { to: "/contacts", label: "Contacts" }): BackTarget {
  if (!state || typeof state !== "object") return fallback;
  const from = (state as { from?: unknown }).from;
  if (typeof from !== "string" || !from.startsWith("/")) return fallback;
  // Preserve query string in `to` so list filters etc. survive the round-trip.
  const path = from.split("?")[0]!;
  for (const k of KNOWN) {
    if (k.match(path)) return { to: from, label: k.label };
  }
  return fallback;
}
